import { connect } from 'react-redux';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React, { useState, useEffect } from 'react';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { Dropdown } from '@commons/utils/styledLibraryComponents';
import { ENUM_COLORS } from '@commons/Text';
import { GenericGapContainer, GenericPageContainer } from '@commons/Layout/styledComponents';
import { getUserTimezone } from '@commons/utils/date';
import { LeftRightSplitter } from '@commons/LeftRightSplitter';
import { PeriodDatePicker } from '@commons/DatePickers/PeriodDatePicker';
import { showErrorMessage } from '@actions/messageconfirmation';
import { usePeriodDatePickerState } from '@hooks/usePeriodDatePickerState';
import cashierService from '@services/cashier';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';

import {
  ContentContainer,
  MetricsContainer,
  LastRowContainer,
  PageContainer,
} from './styledComponents';

import { getSalesPointStores } from '@selectors/stores';
import { STATS_DATA_TYPE } from './components/Metrics';
import Metrics from './components/Metrics';
import TopCashierProducts from './components/TopCashierProducts';

const NUMBER_OF_PRODUCTS_DISPLAYED = 20;

const MappingMonitoring = (props) => {
  const { match, stores, currency, showErrorMessage } = props;

  const USER_TIMEZONE = getUserTimezone();
  const FIRST_DATE_WITH_DATA = moment.tz('2024-12-10', USER_TIMEZONE);
  const YESTERDAY = moment.tz(USER_TIMEZONE).subtract(1, 'day');
  const DEFAULT_START_DATE = moment.max(
    moment.tz(USER_TIMEZONE).subtract(13, 'days'),
    FIRST_DATE_WITH_DATA,
  );
  const currencyCode = currency.alphabeticCode;

  const CHOICES_DROPDOWN_HIDDEN = [
    { id: 1, name: i18next.t('GENERAL.EXCLUDED'), propertyKey: 'hidden', itemValue: true },
    { id: 2, name: i18next.t('GENERAL.UNEXCLUDED'), propertyKey: 'hidden', itemValue: false },
  ];

  const DEFAULT_KEYS_DROPDOWN_HIDDEN = [
    { id: 2, name: i18next.t('GENERAL.UNEXCLUDED'), propertyKey: 'hidden', itemValue: false },
  ];

  /*******************
   ***** STATES *****
   *******************/
  const [isLoading, setIsLoading] = useState(false);

  const [selectedStores, setSelectedStores] = useState(stores);
  const [hiddenKeysForHiddenDropdown, setHiddenKeysForHiddenDropdown] = useState(
    DEFAULT_KEYS_DROPDOWN_HIDDEN,
  );
  const [turnoverMetric, setTurnoverMetric] = useState(0);
  const [turnoverFromProductMixMetric, setTurnoverFromProductMixMetric] = useState(0);
  const [percentageDifferenceMetric, setPercentageDifferenceMetric] = useState(null);
  const [topProducts, setTopProducts] = useState([]);

  const periodPickerState = usePeriodDatePickerState(DEFAULT_START_DATE, YESTERDAY, USER_TIMEZONE);
  const [maxFutureDate, setMaxFutureDate] = useState(null);
  /*******************
   ***** EFFECTS *****
   *******************/

  // Max selectable period is 1 months
  useEffect(() => {
    const maxEndDate = moment.tz(periodPickerState.startDate, USER_TIMEZONE).add(1, 'month');

    setMaxFutureDate(moment.min(YESTERDAY, maxEndDate));
  }, [periodPickerState.startDate]);

  // Fetch metrics on stores and period change
  useEffect(() => {
    if (!selectedStores.length || !periodPickerState.startDate || !periodPickerState.endDate) {
      return;
    }

    (async () => {
      setIsLoading(true);

      try {
        const storeIds = selectedStores.map(({ id }) => id);
        const startDate = moment(periodPickerState.startDate).format(
          DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
        );
        const endDate = moment(periodPickerState.endDate).format(
          DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY,
        );

        const {
          turnoverExcludingTaxes,
          turnoverExcludingTaxesFromProductMix,
          topUnmappedProducts,
        } = await cashierService.getMappingMonitoringByStoreIdsAndDate(
          storeIds,
          startDate,
          endDate,
        );

        const formattedPercentageDifference = !turnoverExcludingTaxes
          ? null
          : 100 -
            100 *
              (Math.min(turnoverExcludingTaxesFromProductMix, turnoverExcludingTaxes) /
                turnoverExcludingTaxes);

        setTurnoverMetric(turnoverExcludingTaxes);
        setTurnoverFromProductMixMetric(
          Math.min(turnoverExcludingTaxesFromProductMix, turnoverExcludingTaxes),
        );
        setPercentageDifferenceMetric(formattedPercentageDifference);
        setTopProducts(topUnmappedProducts);
      } catch {
        showErrorMessage(i18next.t('ADMIN.FTP_DEBUG.FETCH_FTP_STATUS_ERROR'));

        setTurnoverMetric(0);
        setTurnoverFromProductMixMetric(0);
        setPercentageDifferenceMetric(null);
        setTopProducts([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [selectedStores, periodPickerState.startDate, periodPickerState.endDate]);

  const filteredHiddenValues = hiddenKeysForHiddenDropdown.map(({ itemValue }) => itemValue);
  const topFilteredProducts = topProducts
    .filter(({ hidden }) => filteredHiddenValues.includes(hidden))
    .slice(0, NUMBER_OF_PRODUCTS_DISPLAYED)
    .map((product) => ({
      ...product,
      hidden: i18next.t(product.hidden ? 'GENERAL.YES' : 'GENERAL.NO'),
    }));

  return (
    <GenericPageContainer>
      <NavigationBreadCrumb featurePath={match.path} />
      <PageContainer>
        <LeftRightSplitter
          left={
            <GenericGapContainer>
              <Dropdown
                iconSrc="/images/inpulse/pin-black-small.svg"
                isDisabled={isLoading}
                isUniqueSelection={false}
                items={stores}
                searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                selectedItems={selectedStores}
                isRequired
                onSelectionChange={setSelectedStores}
              />
              <PeriodDatePicker
                disabled={isLoading}
                endDate={periodPickerState.endDate}
                focusedDateInput={periodPickerState.focusedDateInput}
                maxFutureDate={maxFutureDate}
                maxPastDate={FIRST_DATE_WITH_DATA}
                setFocusedDateInput={periodPickerState.setFocusedDateInput}
                startDate={periodPickerState.startDate}
                timezone={USER_TIMEZONE}
                onDatesChange={periodPickerState.handleSelectedDates}
              />
              <Dropdown
                iconSrc="/images/inpulse/eye-black-small.svg"
                isDisabled={isLoading}
                isUniqueSelection={false}
                items={CHOICES_DROPDOWN_HIDDEN}
                searchPlaceholder={i18next.t('GENERAL.SEARCH')}
                selectedItems={hiddenKeysForHiddenDropdown}
                isRequired
                onSelectionChange={setHiddenKeysForHiddenDropdown}
              />
            </GenericGapContainer>
          }
        />
        <ContentContainer>
          <div>
            <MetricsContainer>
              <Metrics
                color={ENUM_COLORS.DARKEST}
                currency={currency}
                customStyle={{
                  flex: 1,
                  'min-width': '144px',
                  height: '96px',
                }}
                data={{
                  value: turnoverMetric,
                  type: STATS_DATA_TYPE.CURRENCY,
                }}
                isLoading={isLoading}
                isLoadingPreviousData={isLoading}
                title={i18next.t('ADMIN.MAPPING_MONITORING.METRIC_NAME_TURNOVER', {
                  currencyCode,
                })}
                tooltipText={i18next.t('ADMIN.MAPPING_MONITORING.METRIC_TOOLTIP_TURNOVER')}
              />
              <Metrics
                color={ENUM_COLORS.DARKEST}
                currency={currency}
                customStyle={{
                  flex: 1,
                  'min-width': '144px',
                  height: '96px',
                }}
                data={{
                  value: turnoverFromProductMixMetric,
                  type: STATS_DATA_TYPE.CURRENCY,
                }}
                isLoading={isLoading}
                title={i18next.t('ADMIN.MAPPING_MONITORING.METRIC_NAME_TOTAL_MIX', {
                  currencyCode,
                })}
                tooltipText={i18next.t('ADMIN.MAPPING_MONITORING.METRIC_TOOLTIP_TOTAL_MIX')}
              />
              <Metrics
                color={
                  percentageDifferenceMetric < 5
                    ? ENUM_COLORS.INFO_GREEN
                    : percentageDifferenceMetric < 10
                    ? ENUM_COLORS.INFO_ORANGE
                    : ENUM_COLORS.INFO_RED
                }
                currency={currency}
                customStyle={{
                  flex: 1,
                  'min-width': '144px',
                  height: '96px',
                }}
                data={{
                  value: percentageDifferenceMetric,
                  type: STATS_DATA_TYPE.PERCENT,
                }}
                isLoading={isLoading}
                title={i18next.t('ADMIN.MAPPING_MONITORING.METRIC_NAME_MISSING_MIX', {
                  currencyCode,
                })}
                tooltipText={i18next.t('ADMIN.MAPPING_MONITORING.METRIC_TOOLTIP_MISSING_MIX')}
              />
            </MetricsContainer>
          </div>
          <LastRowContainer>
            <TopCashierProducts
              currency={currency}
              data={topFilteredProducts}
              isLoading={isLoading}
              title={i18next.t('GENERAL.METRIC_MARGIN_EX_TAX', {
                currencyCode,
              })}
              tooltipText={i18next.t('DASHBOARD.FOODCOST.MARGIN_HT_TOOLTIP', {
                currencyCode,
              })}
            />
          </LastRowContainer>
        </ContentContainer>
      </PageContainer>
    </GenericPageContainer>
  );
};

const mapStateToProps = (state) => ({
  stores: getSalesPointStores(state.baseReducer.activeStores),
  currency: state.baseReducer.currency,
});

const mapDispatchToProps = (dispatch) => ({
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MappingMonitoring);
